import { createApi } from "@reduxjs/toolkit/query/react";

import {
  type IPlatformMember,
  type ISendPlatformMemberInvite,
} from "@/providers/store/api/platform-members/platform-members.interface";
import { safeInvalidateTags } from "@/providers/store/api/safe-invalidate-tags";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";


const platformMembersApi = createApi({
  reducerPath: "platformMembersApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.PlatformMembers],
  endpoints: (builder) => ({
    getPlatformMembers: builder.query<IPlatformMember[], void>({
      query: () => ({
        url: "/platform/members",
        method: "GET",
      }),
      providesTags: [ApiTags.PlatformMembers],
      transformResponse: (result: IApiResponse<IPlatformMember[]>) => result.data ?? [],
    }),
    sendPlatformMemberInvite: builder.mutation<void, ISendPlatformMemberInvite>({
      query: ({
        email, role,
      }) => ({
        url: "/platform/members/invite",
        method: "POST",
        body: {
          email,
          role,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.PlatformMembers]),
    }),
    deletePlatformMemberInvite: builder.mutation<void, {  inviteId: string }>({
      query: ({
        inviteId,
      }) => ({
        url: `/platform/members/invite/${inviteId}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.PlatformMembers]),
    }),
    resendPlatformMemberInvite: builder.mutation<void, {  inviteId: string }>({
      query: ({
        inviteId,
      }) => ({
        url: `/platform/members/invite/${inviteId}/resend`,
        method: "POST",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.PlatformMembers]),
    }),
    updatePlatformMemberInvite: builder.mutation<void, { inviteId: string; role: string }>({
      query: ({
        inviteId, role,
      }) => ({
        url: `/platform/members/invite/${inviteId}`,
        method: "PATCH",
        body: {
          role,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.PlatformMembers]),
    }),
    deletePlatformMember: builder.mutation<void, { memberId: string }>({
      query: ({
        memberId,
      }) => ({
        url: `/platform/members/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.PlatformMembers]),
    }),
    updatePlatformMember: builder.mutation<void, { memberId: string; role: string }>({
      query: ({
        memberId, role,
      }) => ({
        url: `/platform/members/${memberId}`,
        method: "PATCH",
        body: {
          role,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.PlatformMembers]),
    }),
  }),
});

export const {
  useGetPlatformMembersQuery,
  useSendPlatformMemberInviteMutation,
  useDeletePlatformMemberInviteMutation,
  useUpdatePlatformMemberInviteMutation,
  useDeletePlatformMemberMutation,
  useUpdatePlatformMemberMutation,
  useResendPlatformMemberInviteMutation,
} = platformMembersApi;
export default platformMembersApi;

